import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Brain, DollarSign, Music, GraduationCap, Bot, FileText, ShoppingBag, Grid, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const { user, isAuthenticated, logout, loading, authError } = useAuth();
  const navRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const nav = navRef.current;
    const update = () => document.documentElement.style.setProperty('--app-nav-bottom', `${nav.getBoundingClientRect().bottom}px`);
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    update();
    return () => observer.disconnect();
  }, []);

  const navCategories = [
    {
      name: 'Business & Finance',
      icon: DollarSign,
      items: [
        { name: 'Business Intelligence', path: '/business' },
        { name: 'Financial Management', path: '/finance' },
        { name: 'Banking & Budgeting', path: '/banking' },
        { name: 'Cryptocurrency', path: '/crypto' }
      ]
    },
    {
      name: 'Creative Studio',
      icon: Music,
      items: [
        { name: "🜂 Sim Forge (forge your agent)", path: '/forge' },
        { name: "🜂 Living SIMs (DNA Lattice)", path: '/lattice' },
        { name: "🜂 Studio Labs (free AI tools)", path: '/labs' },
        { name: '🜂 Speech Studio', path: '/speech' },
        { name: "Aria's Creative Lab", path: '/creativity' },
        { name: 'Music Creation', path: '/music' },
        { name: 'Art & Design', path: '/arts' },
        { name: 'Crafts & Making', path: '/crafts' }
      ]
    },
    {
      name: 'Knowledge & Learning',
      icon: GraduationCap,
      items: [
        { name: 'Education', path: '/education' },
        { name: 'Keyserling Research', path: '/keyserling-research' },
        { name: 'Library', path: '/library' },
        { name: '🜂 Daily Pulse', path: '/daily-pulse' }
      ]
    },
    {
      name: 'AI Systems',
      icon: Bot,
      items: [
        { name: '🜂 Federation Council', path: '/federation' },
        { name: 'AI Minds Directory', path: '/ai' },
        { name: 'Ring of Six', path: '/ring-of-six' },
        { name: 'Ring of Twelve', path: '/ring-of-twelve' },
        { name: 'Persona', path: '/persona' },
        { name: 'SIM Creator', path: '/sims' }
      ]
    },
    {
      name: 'Content & Media',
      icon: FileText,
      items: [
        { name: 'Video Blog', path: '/blogs' },
        { name: 'Podcasts', path: '/podcasts' },
        { name: 'I-FACT Command', path: '/i-fact' }
      ]
    },
    {
      name: 'Federation Store',
      icon: ShoppingBag,
      items: [
        { name: 'Browse Store', path: '/store' },
        { name: 'Shopping Cart', path: '/store/cart' }
      ]
    }
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav ref={navRef} className="app-navigation" data-testid="app-navigation" style={{
        position: 'fixed',
        top: 48,
        left: 0,
        right: 0,
        zIndex: isOpen ? 2147483100 : 1000,
        background: 'rgba(10, 10, 30, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '2px solid rgba(0, 255, 255, 0.3)',
        boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)'
      }}>
        <div className="app-navigation-inner" style={{ 
          maxWidth: '1600px', 
          margin: '0 auto', 
          padding: '0.75rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo/Brand */}
          <Link to="/" data-testid="nav-home-link" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Brain size={32} style={{ color: '#00ffff' }} />
              <span style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                NextXus
              </span>
            </div>
          </Link>

          {/* Log In / Account (httpOnly cookie auth) */}
          {isAuthenticated ? (
            <button
              data-testid="nav-logout-button"
              title={`${user?.name || 'Account'} · Log out`}
              onClick={logout}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.5rem 0.9rem', background: 'rgba(20,14,0,0.95)',
                border: '1px solid #ffd700', borderRadius: '6px', color: '#ffd700',
                cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold',
                letterSpacing: '1px', textTransform: 'uppercase',
              }}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user?.name || 'Profile'}
                  data-testid="nav-user-avatar"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ffd700', flexShrink: 0 }}
                />
              ) : (
                <User size={15} />
              )} {(user?.name || 'Account').split(' ')[0]} · Log Out
            </button>
          ) : (
            <button
              data-testid="nav-login-button"
              disabled={loading}
              aria-busy={loading}
              onClick={() => setShowAuth(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.5rem 0.9rem', background: 'rgba(20,14,0,0.95)',
                border: '1px solid #ffd700', borderRadius: '6px', color: '#ffd700',
                cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold',
                letterSpacing: '1px', textTransform: 'uppercase',
              }}
            >
              <User size={15} /> {loading ? 'Checking…' : 'Log In'}
            </button>
          )}
          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode="login" />
          {authError && <p role="alert" data-testid="nav-auth-error" style={{ color: '#ffb4ad', fontSize: 12 }}>{authError}</p>}

          {/* FULL NEXTXUS Button */}
          <button
            className="nav-full-nextxus"
            data-testid="nav-full-nextxus"
            onClick={() => {
              window.open('https://nextxus.online/command-center.html', 'NextXusGrid', 'width=' + window.screen.width + ',height=' + window.screen.height + ',menubar=no,toolbar=no,location=no');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'rgba(10, 10, 30, 0.95)',
              border: '2px solid #00f0ff',
              borderRadius: '6px',
              color: '#00f0ff',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              transition: 'all 0.3s ease',
              boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)';
              e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(10, 10, 30, 0.95)';
              e.currentTarget.style.boxShadow = '0 0 10px rgba(0, 240, 255, 0.3)';
            }}
          >
            <Grid size={16} />
            Full NextXus
          </button>

          {/* Desktop Menu - visibility handled by .desktop-menu class + <style> tag below */}
          <div style={{ 
            display: 'none',
            gap: '0.5rem'
          }} className="desktop-menu">
            {navCategories.map((category, idx) => {
              const Icon = category.icon;
              return (
                <div 
                  key={category.name}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setOpenDropdown(idx)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button data-testid={`nav-category-${idx}`} aria-expanded={openDropdown === idx} onClick={() => setOpenDropdown(openDropdown === idx ? null : idx)} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: openDropdown === idx ? 'rgba(0, 255, 255, 0.1)' : 'transparent',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '6px',
                    color: '#00ffff',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s ease'
                  }}>
                    <Icon size={16} />
                    {category.name}
                    <ChevronDown size={14} />
                  </button>

                  {/* Dropdown */}
                  {openDropdown === idx && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      paddingTop: '0.5rem',
                      minWidth: '220px',
                      zIndex: 1000,
                    }}>
                    <div style={{
                      background: 'rgba(10, 10, 30, 0.98)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(0, 255, 255, 0.3)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0, 255, 255, 0.2)'
                    }}>
                      {category.items.map((item, itemIdx) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setOpenDropdown(null)}
                          data-testid={`nav-desktop-${item.path.slice(1).replace(/\//g, '-')}`}
                          style={{
                            display: 'block',
                            padding: '0.75rem 1rem',
                            color: location.pathname === item.path ? '#00ff88' : '#fff',
                            textDecoration: 'none',
                            borderBottom: itemIdx < category.items.length - 1 ? '1px solid rgba(0, 255, 255, 0.1)' : 'none',
                            background: location.pathname === item.path ? 'rgba(0, 255, 255, 0.1)' : 'transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (location.pathname !== item.path) {
                              e.target.style.background = 'rgba(0, 255, 255, 0.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (location.pathname !== item.path) {
                              e.target.style.background = 'transparent';
                            }
                          }}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button 
            data-testid="nav-mobile-toggle"
            aria-label={isOpen ? 'Close site menu' : 'Open site menu'}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            style={{
              display: 'block',
              padding: '0.5rem',
              background: 'transparent',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '6px',
              color: '#00ffff',
              cursor: 'pointer'
            }}
            className="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div data-testid="nav-mobile-menu" style={{
          position: 'fixed',
          top: 'var(--app-nav-bottom, 108px)',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483005,
          background: 'rgba(10, 10, 30, 0.98)',
          backdropFilter: 'blur(10px)',
          overflowY: 'auto',
          padding: '1rem 1rem 2rem'
        }}>
          {/* FULL NEXTXUS Button - Mobile */}
          <button
            data-testid="nav-mobile-full-nextxus"
            onClick={() => {
              window.open('https://nextxus.online/command-center.html', 'NextXusGrid', 'width=' + window.screen.width + ',height=' + window.screen.height + ',menubar=no,toolbar=no,location=no');
              setIsOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '1rem',
              background: 'rgba(10, 10, 30, 0.95)',
              border: '2px solid #00f0ff',
              borderRadius: '8px',
              color: '#00f0ff',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginBottom: '1.5rem',
              boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)'
            }}
          >
            <Grid size={20} />
            Full NextXus
          </button>

          {navCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.name} style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  color: '#00ffff',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(0, 255, 255, 0.3)',
                  marginBottom: '0.5rem'
                }}>
                  <Icon size={20} />
                  {category.name}
                </div>
                {category.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-mobile-${item.path.slice(1).replace(/\//g, '-')}`}
                    onClick={() => setIsOpen(false)}
                    style={{
                      display: 'block',
                      padding: '0.75rem 1.5rem',
                      color: location.pathname === item.path ? '#00ff88' : '#fff',
                      textDecoration: 'none',
                      background: location.pathname === item.path ? 'rgba(0, 255, 255, 0.1)' : 'transparent',
                      borderLeft: location.pathname === item.path ? '3px solid #00ff88' : '3px solid transparent',
                      marginBottom: '0.25rem'
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Spacer to prevent content from hiding under fixed pillars + nav */}
      <div style={{ height: 'var(--app-nav-bottom, 108px)' }} />

      <style>{`
        .app-navigation-inner { flex-wrap: nowrap !important; gap: 12px !important; }
        .app-navigation [data-testid="nav-home-link"] { flex-shrink: 0; }
        .app-navigation [data-testid="nav-home-link"] span { white-space: nowrap; }
        .app-navigation [data-testid="nav-logout-button"] { max-width: 190px; overflow-wrap: anywhere; }
        @media (min-width: 901px) { .app-navigation { left: 280px !important; } }
        @media (max-width: 600px) {
          .app-navigation .nav-full-nextxus { display: none !important; }
          .app-navigation-inner { justify-content: space-between !important; padding: 10px 12px !important; }
          .app-navigation [data-testid="nav-home-link"] span { font-size: 18px !important; }
          .app-navigation [data-testid="nav-home-link"] svg { width: 22px; }
          .app-navigation [data-testid="nav-logout-button"] { max-width: 135px; font-size: 10px !important; }
        }
        @media (min-width: 1600px) {
          .desktop-menu {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Navigation;
